import { emptyVariantDraft, type ProductVariantDraft } from './productDraft';

// ── Presets de valores para ejes comunes ──
export const VARIANT_SIZE_PRESETS: Record<string, string[]> = {
  letters: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  shoes: ['35', '36', '37', '38', '39', '40', '41', '42'],
  rings: ['16', '18', '20'],
  unique: ['Único'],
};

export const VARIANT_SIZE_PRESET_CHIPS: Array<{ key: keyof typeof VARIANT_SIZE_PRESETS; label: string }> = [
  { key: 'letters', label: 'Talles ropa' },
  { key: 'shoes', label: 'Talles calzado' },
  { key: 'rings', label: 'Anillos' },
  { key: 'unique', label: 'Único' },
];

// ── Presets de nombres de ejes ──
// El dueño puede elegir uno de estos o escribir "Personalizado".
export const AXIS_NAME_PRESETS: Array<{ name: string; label: string; values?: string[] }> = [
  { name: 'talle', label: 'Talle', values: VARIANT_SIZE_PRESETS.letters },
  { name: 'color', label: 'Color', values: ['Rojo', 'Azul', 'Negro', 'Blanco', 'Verde', 'Amarillo', 'Rosa', 'Gris', 'Marrón', 'Naranja', 'Violeta', 'Beige'] },
  { name: 'material', label: 'Material', values: ['Madera', 'Metal', 'Plástico', 'Cuero', 'Vidrio'] },
  { name: 'tono', label: 'Tono', values: ['Claro', 'Medio', 'Oscuro'] },
  { name: 'tamano', label: 'Tamaño', values: ['Chico', 'Mediano', 'Grande'] },
  { name: 'capacidad', label: 'Capacidad', values: ['250ml', '500ml', '1L', '2L'] },
  { name: 'fragancia', label: 'Fragancia', values: ['Lavanda', 'Vainilla', 'Cítrico', 'Floral'] },
  { name: 'acabado', label: 'Acabado', values: ['Mate', 'Brillo', 'Pulido'] },
];

export const MAX_AXES = 3;

export type AxisInput = {
  name: string;
  values: string;
};

export function parseAxisValues(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[,;|]/)) {
    const value = part.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

function cellKey(attrs: Record<string, string>, axisNames: string[]): string {
  return axisNames
    .map((n) => (attrs[n] ?? '').trim().toLowerCase())
    .join('|');
}

function buildNameSuffix(attrs: Record<string, string>, axisNames: string[]): string {
  return axisNames
    .map((n) => attrs[n]?.trim())
    .filter(Boolean)
    .join(' · ');
}

/**
 * Producto cartesiano de N ejes.
 * Ej: [[M, L], [Rojo, Azul]] → [{talle:M,color:Rojo}, {talle:M,color:Azul}, {talle:L,color:Rojo}, {talle:L,color:Azul}]
 */
function cartesianProduct(axes: Array<{ name: string; values: string[] }>): Record<string, string>[] {
  if (axes.length === 0) return [];
  const [first, ...rest] = axes;
  const restCombos = cartesianProduct(rest);
  const result: Record<string, string>[] = [];
  for (const val of first.values) {
    if (restCombos.length === 0) {
      result.push({ [first.name]: val });
    } else {
      for (const combo of restCombos) {
        result.push({ [first.name]: val, ...combo });
      }
    }
  }
  return result;
}

export function buildVariantMatrixDrafts(
  existing: ProductVariantDraft[],
  axes: Array<{ name: string; values: string[] }>,
  defaultPrice = '',
): ProductVariantDraft[] {
  const validAxes = axes.filter((a) => a.name.trim() && a.values.length > 0);
  if (validAxes.length === 0) return existing;

  const axisNames = validAxes.map((a) => a.name.trim());

  const existingByCell = new Map(
    existing.map((row) => [cellKey(row.attributes, axisNames), row]),
  );

  const combos = cartesianProduct(validAxes);
  const next: ProductVariantDraft[] = [];

  for (const combo of combos) {
    const key = cellKey(combo, axisNames);
    const prev = existingByCell.get(key);
    const nameSuffix = buildNameSuffix(combo, axisNames);
    if (prev) {
      next.push({
        ...prev,
        attributes: { ...combo },
        nameSuffix: prev.nameSuffix.trim() || nameSuffix,
        price: prev.price || defaultPrice,
      });
      continue;
    }
    next.push({
      ...emptyVariantDraft(),
      attributes: { ...combo },
      nameSuffix,
      price: defaultPrice,
    });
  }
  return next;
}

export function suggestInternalVariantBarcode(seed: string): string {
  const digits = seed.replace(/\D/g, '').slice(-9).padStart(9, '0');
  return `299${digits}`.slice(0, 13);
}

/**
 * Extrae los nombres de ejes únicos de una lista de variantes.
 * Ej: [{attributes:{talle:"M",color:"Rojo"}}, ...] → ["talle", "color"]
 */
export function extractAxisNames(drafts: ProductVariantDraft[]): string[] {
  const seen = new Set<string>();
  for (const d of drafts) {
    for (const k of Object.keys(d.attributes)) {
      if (d.attributes[k]?.trim()) seen.add(k);
    }
  }
  return Array.from(seen);
}
